"use client";



import Link from "next/link";

import { useEffect, useRef, useState } from "react";

import type { Map as LeafletMap } from "leaflet";

import { compressDriverDocument } from "@/lib/compressDriverDocument";
import { buildPassengerRequest, isRidePoint, parseRideEstimate, type PassengerRequestInput, type RideEstimate, type RidePoint } from "@/lib/homepageRideModes";



type Props = { mode: "Now" | "Scheduled"; pickup: RidePoint | null; destination: RidePoint | null; map: LeafletMap | null; loggedIn: boolean; showForm?: boolean; onTripVisibilityChange?: (visible: boolean) => void; onActiveTripChange?: (active: boolean) => void; onSearchEnded?: () => void; onRestoreActiveRide?: (trip: HomepagePassengerTrip) => void };

type RouteState = { key: string; estimate: RideEstimate | null; loading: boolean; error: string };



type HomepagePassengerTrip = {

  id: number;

  requestMode?: string | null;

  status: string;
  paymentAmount?: number | null;
  negotiationStatus?: string | null;
  finalAgreedFare?: number | null;

  fromLocation: string;

  toLocation: string;

  fromLatitude?: number | null;

  fromLongitude?: number | null;

  toLatitude?: number | null;

  toLongitude?: number | null;

  rideNowExpiresAt?: string | null;

  driverArrivedAt?: string | null;

  otpVerified?: boolean;

  startOtp?: string | null;
  arrivalConfirmed?: boolean;

  driver?: {

    name: string;

    driverLiveLatitude?: number | null;

    driverLiveLongitude?: number | null;

    driverLiveLocationUpdatedAt?: string | null;

  } | null;

};







export default function PassengerRideRequestPanel({ mode, pickup, destination, map, loggedIn, showForm = true, onTripVisibilityChange, onActiveTripChange, onSearchEnded, onRestoreActiveRide }: Props) {

  const [passengerTrip, setPassengerTrip] = useState<HomepagePassengerTrip | null>(null);
  // Keep the Ride Now DRIVER marker stable between GPS refreshes.
  const liveDriverMarkerRef = useRef<import("leaflet").CircleMarker | null>(null);
  const [searchingTrip, setSearchingTrip] = useState<HomepagePassengerTrip | null>(null);
  const [searchNowMs, setSearchNowMs] = useState(() => Date.now());
  const [searchCancelBusy, setSearchCancelBusy] = useState(false);
  const [searchCancelError, setSearchCancelError] = useState("");
  // Update the Searching countdown every second without refreshing.
  useEffect(() => {
    if (mode !== "Now" || !searchingTrip) return;

    setSearchNowMs(Date.now());

    const timer = window.setInterval(() => {
      setSearchNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [mode, searchingTrip?.id]);

  const expiredSearchHandledRef = useRef<number | null>(null);

  useEffect(() => {
    if (mode !== "Now" || !searchingTrip?.rideNowExpiresAt) return;

    const expiresAt = new Date(searchingTrip.rideNowExpiresAt).getTime();
    if (!Number.isFinite(expiresAt) || searchNowMs < expiresAt) return;
    if (expiredSearchHandledRef.current === searchingTrip.id) return;

    const requestId = searchingTrip.id;
    let cancelled = false;

    async function confirmSearchEnd() {
      try {
        const response = await fetch("/api/ride-requests?mode=mine", {
          cache: "no-store",
        });
        if (!response.ok) return;

        const data: unknown = await response.json();
        if (cancelled || !Array.isArray(data)) return;

        const current = (data as HomepagePassengerTrip[]).find(
          (trip) => trip.id === requestId && trip.requestMode === "Now"
        );
        if (!current) return;

        if (current.status === "Accepted" || current.status === "Started") {
          setSearchingTrip(null);
          setPassengerTrip(current);
          return;
        }

        if (current.status !== "Expired" && current.status !== "Cancelled") return;

        expiredSearchHandledRef.current = requestId;
        setSearchingTrip(null);
        setPosted(false);
        onSearchEnded?.();
      } catch {
        // Keep the existing state if the server cannot confirm expiry.
      }
    }

    void confirmSearchEnd();
    return () => { cancelled = true; };
  }, [mode, searchingTrip, searchNowMs, onSearchEnded]);

  const [passengerTripError, setPassengerTripError] = useState("");
  const [arrivalBusy, setArrivalBusy] = useState(false);
  const [arrivalError, setArrivalError] = useState("");
  const [dismissedCompletedId, setDismissedCompletedId] = useState<number | null>(null);

  const [bookingType, setBookingType] = useState<"Seat" | "Parcel">("Seat");
  const [parcelDescription, setParcelDescription] = useState("");
  const [parcelWeightKg, setParcelWeightKg] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [parcelPhoto, setParcelPhoto] = useState<File | null>(null);
  const [parcelPreview, setParcelPreview] = useState<string | null>(null);
  const [parcelCompressing, setParcelCompressing] = useState(false);
  const parcelCompressionId = useRef(0);
  const [seats, setSeats] = useState("1");

  const [vehicle, setVehicle] = useState("Any");

  const [contribution, setContribution] = useState("");

  const [date, setDate] = useState("");

  const [hour, setHour] = useState("");

  const [minute, setMinute] = useState("00");

  const [period, setPeriod] = useState<"AM" | "PM">("AM");

  const [routeState, setRouteState] = useState<RouteState>({ key: "", estimate: null, loading: false, error: "" });

  const [refreshRoute, setRefreshRoute] = useState(0);

  const [review, setReview] = useState<PassengerRequestInput | null>(null);

  const [error, setError] = useState("");

  const [pending, setPending] = useState(false);

  const [posted, setPosted] = useState(false);

  const [uncertain, setUncertain] = useState(false);

  const [needsLogin, setNeedsLogin] = useState(false);

  const pendingRef = useRef(false);

  const dialogRef = useRef<HTMLDialogElement>(null);



  // Use the existing Passenger booking API. No new GPS watcher or booking.

  useEffect(() => {

    if (!loggedIn || mode !== "Now") {
      setPassengerTrip(null);
      setSearchingTrip(null);
      setPassengerTripError("");
      return;
    }



    let active = true;



    async function refreshPassengerTrip() {

      try {

        const response = await fetch(

          "/api/ride-requests?mode=mine",

          { cache: "no-store" }

        );



        if (!response.ok) {

          throw new Error("Unable to refresh Passenger booking status.");

        }



        const data: unknown = await response.json();

        if (!active) return;



        const trips = Array.isArray(data)

          ? data as HomepagePassengerTrip[]

          : [];



        const searchingRide = trips.find(
          (trip) =>
            trip.requestMode === "Now" &&
            trip.status === "Open" &&
            !!trip.rideNowExpiresAt &&
            new Date(trip.rideNowExpiresAt).getTime() > Date.now()
        ) ?? null;

        setSearchingTrip(searchingRide);

        // Restore saved pickup/destination for both Seat and Parcel
        // while the Ride Now searching countdown is active.
        if (searchingRide) {
          onRestoreActiveRide?.(searchingRide);
        }

        const activeRide = trips.find(
          (trip) => trip.requestMode === "Now" &&
            (trip.status === "Accepted" || trip.status === "Started")
        ) ?? null;

        if (activeRide) {
          onRestoreActiveRide?.(activeRide);
          window.sessionStorage.setItem(
            "apnasaathi:passenger-home-trip-id", String(activeRide.id)
          );
        }
        const savedId = Number(
          window.sessionStorage.getItem("apnasaathi:passenger-home-trip-id")
        );
        const completedRide = Number.isSafeInteger(savedId) && savedId > 0
          ? trips.find((trip) =>
              trip.requestMode === "Now" && trip.id === savedId &&
              trip.status === "Completed"
            ) ?? null
          : null;
        setPassengerTrip(activeRide ??
          (completedRide?.id === dismissedCompletedId ? null : completedRide)
        );

        setPassengerTripError("");

      } catch (error) {

        if (active) {

          setPassengerTripError(

            error instanceof Error ? error.message : "Booking status unavailable."

          );

        }

      }

    }



    void refreshPassengerTrip();

    const timer = window.setInterval(() => void refreshPassengerTrip(), 5000);



    return () => {

      active = false;

      window.clearInterval(timer);

    };

  }, [loggedIn, mode, dismissedCompletedId]);





  // Passenger active trip road route on the EXISTING Homepage Leaflet map.

  // Remove the DRIVER marker when no active Ride Now trip remains.
  // Preserve it during temporary network errors while the trip is active.
  useEffect(() => {
    if (mode === "Now" && passengerTrip &&
        (passengerTrip.status === "Accepted" || passengerTrip.status === "Started")) {
      return;
    }

    if (liveDriverMarkerRef.current && map) {
      map.removeLayer(liveDriverMarkerRef.current);
    }
    liveDriverMarkerRef.current = null;
  }, [map, mode, passengerTrip?.id, passengerTrip?.status]);

  useEffect(() => {
    return () => {
      if (liveDriverMarkerRef.current && map) {
        map.removeLayer(liveDriverMarkerRef.current);
        liveDriverMarkerRef.current = null;
      }
    };
  }, [map]);

  // The Passenger API only exposes driver GPS when it is fresh and authorized.

  useEffect(() => {

    if (!map || mode !== "Now" || !passengerTrip || passengerTrip.status === "Completed") return;



    const trip = passengerTrip;

    const driverLat = trip.driver?.driverLiveLatitude;

    const driverLng = trip.driver?.driverLiveLongitude;

    const updatedAt = trip.driver?.driverLiveLocationUpdatedAt;

    const toDestination = trip.status === "Started";

    const targetLat = toDestination ? trip.toLatitude : trip.fromLatitude;

    const targetLng = toDestination ? trip.toLongitude : trip.fromLongitude;



    const valid = (

      value: number | null | undefined,

      min: number,

      max: number

    ): value is number =>

      typeof value === "number" && Number.isFinite(value) &&

      value >= min && value <= max;



    // Do not draw a falsely live driver marker from missing or outdated GPS.

    const gpsTime = updatedAt ? new Date(updatedAt).getTime() : NaN;

    if (

      !valid(driverLat, -90, 90) || !valid(driverLng, -180, 180) ||

      !valid(targetLat, -90, 90) || !valid(targetLng, -180, 180) ||

      (driverLat === 0 && driverLng === 0) ||

      !Number.isFinite(gpsTime) || gpsTime > Date.now() ||

      Date.now() - gpsTime > 90000

    ) return;



    const driver: [number, number] = [driverLat, driverLng];

    const target: [number, number] = [targetLat, targetLng];

    const controller = new AbortController();

    let cancelled = false;

    let layer: import("leaflet").LayerGroup | null = null;



    async function drawTripRoute() {

      try {

        const module = await import("leaflet");

        if (cancelled || !map) return;

        const L = module.default ?? module;



        layer = L.layerGroup().addTo(map);



        // Reuse the DRIVER marker instead of recreating it on every refresh.
        if (liveDriverMarkerRef.current) {
          liveDriverMarkerRef.current.setLatLng(driver);
        } else {
          liveDriverMarkerRef.current = L.circleMarker(driver, {
            radius: 11,
            color: "#ffffff",
            weight: 3,
            fillColor: "#2563eb",
            fillOpacity: 1,
          }).bindTooltip("🚗 Your DRIVER").addTo(map);
        }



        L.circleMarker(target, {

          radius: 10,

          color: "#ffffff",

          weight: 3,

          fillColor: toDestination ? "#059669" : "#e11d48",

          fillOpacity: 1,

        }).bindTooltip(toDestination ? "🏁 Destination" : "📍 Pickup").addTo(layer);



        const coordinates =

          `${driver[1]},${driver[0]};${target[1]},${target[0]}`;



        const response = await fetch(

          `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&steps=false`,

          { cache: "no-store", signal: controller.signal }

        );



        if (!response.ok) return;



        const data = await response.json();

        const geometry = data?.routes?.[0]?.geometry?.coordinates;



        if (cancelled || !layer || data?.code !== "Ok" ||

            !Array.isArray(geometry)) return;



        const points: [number, number][] = geometry

          .filter((point: unknown): point is number[] =>

            Array.isArray(point) && point.length >= 2 &&

            Number.isFinite(point[0]) && Number.isFinite(point[1]))

          .map((point: number[]) => [point[1], point[0]]);



        if (points.length < 2) return;



        L.polyline(points, {

          color: toDestination ? "#059669" : "#4f46e5",

          weight: 6,

          opacity: 0.95,

          interactive: false,

        }).addTo(layer);



        // Focus once per booking and phase, not every 5-second GPS update.

        const focusMap = map as LeafletMap & {

          _apnasaathiPassengerTripFocusKey?: string;

        };

        const focusKey = `${trip.id}:${toDestination ? "destination" : "pickup"}`;

        if (focusMap._apnasaathiPassengerTripFocusKey !== focusKey) {

          focusMap._apnasaathiPassengerTripFocusKey = focusKey;

          map.fitBounds(L.latLngBounds(points), {

            padding: [60, 60],

            maxZoom: 16,

            animate: true,

          });

        }

      } catch {

        // Booking and OTP remain accessible if routing service fails.

      }

    }



    void drawTripRoute();



    return () => {

      cancelled = true;

      controller.abort();

      if (layer && map) map.removeLayer(layer);

    };

  }, [map, mode, passengerTrip]);



  useEffect(() => {

    const searchingActive =
      mode === "Now" &&
      !!searchingTrip &&
      !!searchingTrip.rideNowExpiresAt &&
      new Date(searchingTrip.rideNowExpiresAt).getTime() > searchNowMs;

    onTripVisibilityChange?.(!!passengerTrip || searchingActive);
    onActiveTripChange?.(
      searchingActive ||
      passengerTrip?.status === "Accepted" ||
      passengerTrip?.status === "Started"
    );

  }, [mode, passengerTrip, searchingTrip, searchNowMs, onTripVisibilityChange, onActiveTripChange]);



  const pickupLat = pickup?.latitude, pickupLng = pickup?.longitude;

  const destinationLat = destination?.latitude, destinationLng = destination?.longitude;

  const routeKey = `${pickupLat}:${pickupLng}:${destinationLat}:${destinationLng}`;

  const currentRoute = routeState.key === routeKey ? routeState : null;

  const exactTrip = isRidePoint(pickup) && isRidePoint(destination);



  useEffect(() => {

    if (!showForm || pickupLat === undefined || pickupLng === undefined || destinationLat === undefined || destinationLng === undefined) return;

    const controller = new AbortController();

    async function loadRoute() {

      setRouteState({ key: routeKey, estimate: null, loading: true, error: "" });

      try {

        const params = new URLSearchParams({ fromLatitude: String(pickupLat), fromLongitude: String(pickupLng), toLatitude: String(destinationLat), toLongitude: String(destinationLng) });

        const response = await fetch(`/api/route-estimate?${params}`, { cache: "no-store", signal: controller.signal });

        const data = await response.json();

        if (!response.ok) throw new Error(data.message || "Road route unavailable. Try again.");

        const estimate = parseRideEstimate(data);

        if (!controller.signal.aborted) setRouteState({ key: routeKey, estimate, loading: false, error: "" });

      } catch (cause) {

        if (!controller.signal.aborted) setRouteState({ key: routeKey, estimate: null, loading: false, error: cause instanceof Error ? cause.message : "Road route unavailable. Try again." });

      }

    }

    // Only coordinate selection triggers routing; map panning does not make route calls.

    const timer = setTimeout(() => void loadRoute(), 350);

    return () => { clearTimeout(timer); controller.abort(); };

  }, [pickupLat, pickupLng, destinationLat, destinationLng, routeKey, refreshRoute, showForm]);



  const estimate = exactTrip ? currentRoute?.estimate : null;

  useEffect(() => {

    let cancelled = false;

    let layer: import("leaflet").Polyline | null = null;

    if (!map || !estimate || !showForm) return;

    void import("leaflet").then(L => {

      if (cancelled) return;

      layer = L.polyline(estimate.coordinates, { color: "#4f46e5", weight: 4, opacity: 0.75, interactive: false }).addTo(map);

      map.fitBounds(layer.getBounds(), { padding: [30, 30], maxZoom: 14 });

    }).catch(() => { /* The request form remains usable if its decorative route cannot render. */ });

    return () => { cancelled = true; layer?.remove(); };

  }, [map, estimate, showForm]);



  useEffect(() => {

    const dialog = dialogRef.current;

    if (review && dialog && !dialog.open) dialog.showModal();

    return () => dialog?.close();

  }, [review]);



  // Passenger confirmation is the single authorized completion action.
  // There is intentionally no separate DRIVER Complete mutation.
  async function confirmDestinationArrival() {
    const trip = passengerTrip;
    if (!trip || trip.status !== "Started" || arrivalBusy) return;
    if (!window.confirm("Have you reached your destination? This will complete the ride.")) return;

    setArrivalBusy(true);
    setArrivalError("");
    try {
      const response = await fetch(`/api/ride-requests/${trip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirm-arrival" }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Could not confirm destination arrival.");
      }

      // Immediate visual feedback; 5-second API polling remains authoritative.
      setPassengerTrip((previous) => previous?.id === trip.id
        ? { ...previous, status: "Completed", arrivalConfirmed: true }
        : previous
      );
      window.sessionStorage.setItem(
        "apnasaathi:passenger-home-trip-id", String(trip.id)
      );
    } catch (cause) {
      setArrivalError(cause instanceof Error
        ? cause.message : "Could not confirm destination arrival.");
    } finally {
      setArrivalBusy(false);
    }
  }

  function dismissCompletedTrip() {
    if (!passengerTrip || passengerTrip.status !== "Completed") return;
    setDismissedCompletedId(passengerTrip.id);
    window.sessionStorage.removeItem("apnasaathi:passenger-home-trip-id");
    setPassengerTrip(null);
    setArrivalError("");
  }

  async function cancelSearchingRide() {
    const trip = searchingTrip;
    if (!trip || searchCancelBusy || trip.status !== "Open") return;
    if (!window.confirm("Are you sure you want to cancel this ride request?")) return;

    setSearchCancelBusy(true);
    setSearchCancelError("");

    try {
      const response = await fetch(`/api/ride-requests/${trip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          cancelReason: "PassengerChangedMind",
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof result.message === "string"
            ? result.message
            : "Could not cancel the request."
        );
      }

      setSearchingTrip((current) => current?.id === trip.id ? null : current);
      onSearchEnded?.();
      setPosted(false);
      setUncertain(false);
      setReview(null);
    } catch (cause) {
      setSearchCancelError(
        cause instanceof Error ? cause.message : "Could not cancel the request."
      );
    } finally {
      setSearchCancelBusy(false);
    }
  }

  const [fareAcceptBusy, setFareAcceptBusy] = useState(false);
  const [fareAcceptError, setFareAcceptError] = useState("");

  async function acceptDriverFare() {
    const trip = passengerTrip;
    if (
      !trip ||
      trip.requestMode !== "Now" ||
      trip.status !== "Accepted" ||
      trip.negotiationStatus === "ACCEPTED" ||
      fareAcceptBusy
    ) return;

    setFareAcceptBusy(true);
    setFareAcceptError("");

    try {
      const response = await fetch(`/api/ride-requests/${trip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "accept-fare" }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof result.message === "string"
            ? result.message
            : "Could not accept driver fare."
        );
      }

      setPassengerTrip(current =>
        current?.id === trip.id
          ? {
              ...current,
              negotiationStatus: "ACCEPTED",
              finalAgreedFare: result.finalAgreedFare,
            }
          : current
      );
    } catch (cause) {
      setFareAcceptError(
        cause instanceof Error
          ? cause.message
          : "Could not accept driver fare."
      );
    } finally {
      setFareAcceptBusy(false);
    }
  }

  const [acceptedCancelBusy, setAcceptedCancelBusy] = useState(false);
  const [acceptedCancelError, setAcceptedCancelError] = useState("");

  async function cancelAcceptedRide() {
    const trip = passengerTrip;
    if (!trip || trip.status !== "Accepted" || acceptedCancelBusy) return;

    if (!window.confirm(
      "DRIVER ka amount pasand nahi aaya? Kya aap booking cancel karna chahte hain?"
    )) return;

    setAcceptedCancelBusy(true);
    setAcceptedCancelError("");

    try {
      const response = await fetch(`/api/ride-requests/${trip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          cancelReason: "DriverFareNotAccepted",
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof result.message === "string"
            ? result.message
            : "Could not cancel booking."
        );
      }

      setPassengerTrip(null);
      setSearchingTrip(null);
      window.sessionStorage.removeItem("apnasaathi:passenger-home-trip-id");
      onTripVisibilityChange?.(false);
      onActiveTripChange?.(false);
      onSearchEnded?.();
      setPosted(false);
      setUncertain(false);
      setReview(null);
    } catch (cause) {
      setAcceptedCancelError(
        cause instanceof Error ? cause.message : "Could not cancel booking."
      );
    } finally {
      setAcceptedCancelBusy(false);
    }
  }

  function openReview() {

    setError("");

    if (posted || uncertain || pendingRef.current) return;
    if (mode === "Now" && bookingType === "Parcel" &&
        (parcelCompressing || !parcelPhoto ||
         parcelPhoto.size > 100 * 1024)) {
      setError(parcelCompressing
        ? "Please wait for parcel photo compression."
        : "Select a valid parcel photo within 100 KB.");
      return;
    }


    if (!loggedIn || needsLogin) { setError("Log in to send a ride request."); return; }

    const input: PassengerRequestInput = {
      mode, pickup, destination, route: estimate ?? null,
      seats: mode === "Now" && bookingType === "Parcel" ? "1" : seats,
      vehicle, contribution, date, hour, minute, period,
      bookingType: mode === "Now" ? bookingType : "Seat",
      parcelDescription: mode === "Now" && bookingType === "Parcel"
        ? parcelDescription.trim() : undefined,
      parcelWeightKg: mode === "Now" && bookingType === "Parcel"
        ? parcelWeightKg : undefined,
      receiverPhone: mode === "Now" && bookingType === "Parcel"
        ? receiverPhone : undefined,
    };

    try {

      buildPassengerRequest(input);

      setReview(input);

    } catch (cause) { setError(cause instanceof Error ? cause.message : "Please check your request."); }

  }



  function closeReview() {

    if (pendingRef.current) return;

    setReview(null);

    // A completed/uncertain submission remains blocked until the user checks My Requests.

  }



  async function confirmRequest() {

    if (!review || pendingRef.current || posted || uncertain || needsLogin) return;

    let payload: ReturnType<typeof buildPassengerRequest>;

    try { payload = buildPassengerRequest(review); }

    catch (cause) { setError(cause instanceof Error ? cause.message : "Please check your request."); return; }

    if (review.mode === "Now" && review.bookingType === "Parcel" &&
        (parcelCompressing || !parcelPhoto ||
         parcelPhoto.size > 100 * 1024)) {
      setError(parcelCompressing
        ? "Please wait for parcel photo compression."
        : "Select a valid parcel photo within 100 KB.");
      return;
    }

    pendingRef.current = true;

    setPending(true);

    setError("");

    try {

      if (review.mode === "Now" && review.bookingType === "Parcel") {
        if (!parcelPhoto) {
          setError("Select a parcel photo before booking.");
          return;
        }

        // Upload happens before the booking POST. An upload failure
        // does not mean a ride request was created.
        try {
          const formData = new FormData();
          formData.append("file", parcelPhoto);

          const uploadResponse = await fetch("/api/parcel-upload", {
            method: "POST",
            body: formData,
          });

          const uploadData = await uploadResponse.json();

          if (!uploadResponse.ok || typeof uploadData.image !== "string") {
            throw new Error(uploadData.message || "Parcel photo upload failed.");
          }

          payload.parcelImage = uploadData.image;
        } catch (uploadError) {
          setError(uploadError instanceof Error
            ? uploadError.message
            : "Parcel photo upload failed. Please try again.");
          return;
        }
      }

      const response = await fetch("/api/ride-requests", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });

      const data = await response.json();

      if (!response.ok) {

        if (response.status === 401) setNeedsLogin(true);

        if (response.status >= 500) setUncertain(true);

        setError(data.message || "Unable to post ride request.");

        return;

      }

      if (!Number.isSafeInteger(data.id)) throw new Error("Request response could not be verified.");

      if (mode === "Now") {
        if (typeof data.rideNowExpiresAt !== "string" ||
            !Number.isFinite(new Date(data.rideNowExpiresAt).getTime())) {
          setUncertain(true);
          setError("Booking posted, but search expiry could not be verified. Check My Requests.");
          return;
        }

        setSearchingTrip({
          id: data.id,
          requestMode: "Now",
          status: "Open",
          rideNowExpiresAt: data.rideNowExpiresAt,
          fromLocation: data.fromLocation,
          toLocation: data.toLocation,
        });
        setSearchNowMs(Date.now());
        setReview(null);
      } else {
        setPosted(true);
      }

    } catch {

      setUncertain(true);

      setError("Request result could not be confirmed. Check My Requests before trying again.");

    } finally { pendingRef.current = false; setPending(false); }

  }



  const status = posted ? "Request posted successfully." : uncertain ? "Check My Requests before posting again." : "";

  const totalLabel = mode === "Now" ? "Ride Now" : "Scheduled Request";

  const blocked = pending || posted || uncertain || (mode === "Now" && (passengerTrip?.status === "Accepted" || passengerTrip?.status === "Started"));

  return <div className="space-y-2 border-t border-slate-100 pt-2">

    {mode === "Now" && searchingTrip && (
      <div className="space-y-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm">
        <p className="text-center text-sm font-black text-amber-900">
          <span className="animate-pulse">🔎</span> Searching for nearby available drivers...
        </p>

        <div className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-center">
          <p className="text-xs font-semibold text-amber-700">⏱️ Time remaining</p>
          <p className="mt-1 text-2xl font-black tabular-nums text-amber-900">
            {Math.floor(Math.max(0, new Date(searchingTrip.rideNowExpiresAt ?? 0).getTime() - searchNowMs) / 60000)}:
            {String(Math.floor(Math.max(0, new Date(searchingTrip.rideNowExpiresAt ?? 0).getTime() - searchNowMs) / 1000) % 60).padStart(2, "0")}
          </p>
        </div>

        <p className="text-center text-xs font-medium text-amber-800">
          Nearby DRIVER acceptance is checked automatically every 5 seconds.
        </p>

        <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700">
          <p><strong>Pickup:</strong> {searchingTrip.fromLocation}</p>
          <p className="mt-1"><strong>Destination:</strong> {searchingTrip.toLocation}</p>
        </div>

        {searchCancelError && (
          <p role="alert" className="text-xs font-semibold text-red-700">
            {searchCancelError}
          </p>
        )}

        <button
          type="button"
          disabled={searchCancelBusy}
          onClick={() => void cancelSearchingRide()}
          className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
        >
          {searchCancelBusy ? "Cancelling..." : "Cancel Request"}
        </button>
      </div>
    )}

    {mode === "Now" && passengerTrip && (

      <div className="space-y-2 rounded-xl border-2 border-emerald-400 bg-emerald-50 p-3">

        <h3 className="text-sm font-bold text-emerald-900">

          {passengerTrip.status === "Completed" ? "✅ Ride Completed" : "🚗 Booking Confirmed"} · Ride #{passengerTrip.id}

        </h3>

        <p className="text-xs text-slate-800">

          <strong>DRIVER:</strong> {passengerTrip.driver?.name || "Assigned"}

        </p>

        <p className="text-xs text-slate-800">

          <strong>Pickup:</strong> {passengerTrip.fromLocation}

        </p>

        <p className="text-xs text-slate-800">

          <strong>Destination:</strong> {passengerTrip.toLocation}

        </p>

        <p className="text-xs font-semibold text-indigo-800">

          {passengerTrip.status === "Completed"

            ? "✅ Destination reached — Trip completed"

            : passengerTrip.status === "Started"

              ? "🛣️ Trip Started — Travelling to destination"

              : passengerTrip.driverArrivedAt

              ? "📍 DRIVER Arrived at pickup"

              : "🚗 DRIVER Arriving at pickup"}

        </p>

        {passengerTrip.status === "Accepted" && (
          <div className="space-y-2 rounded-xl border border-rose-200 bg-white p-3">
            <div className="rounded-lg bg-indigo-50 p-3 text-center">
              <p className="text-xs font-semibold text-indigo-800">
                DRIVER Accepted Fare
              </p>
              <p className="text-2xl font-black text-indigo-950">
                {typeof passengerTrip.paymentAmount === "number" &&
                 Number.isFinite(passengerTrip.paymentAmount)
                  ? `₹${passengerTrip.paymentAmount.toFixed(2)}`
                  : "Amount unavailable"}
              </p>
            </div>
            {passengerTrip.negotiationStatus === "ACCEPTED" ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-bold text-emerald-700">
                ✅ Fare Accepted
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void acceptDriverFare()}
                disabled={fareAcceptBusy || acceptedCancelBusy}
                className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {fareAcceptBusy ? "Accepting..." : "✅ Accept Fare"}
              </button>
            )}
            {fareAcceptError && (
              <p role="alert" className="text-xs text-red-700">
                {fareAcceptError}
              </p>
            )}
            {passengerTrip.negotiationStatus !== "ACCEPTED" && (
            <button
              type="button"
              onClick={() => void cancelAcceptedRide()}
              disabled={acceptedCancelBusy}
              className="w-full rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 disabled:opacity-50"
            >
              {acceptedCancelBusy ? "Cancelling..." : "Cancel Booking — Fare Not Accepted"}
            </button>
            )}
            {acceptedCancelError && (
              <p role="alert" className="text-xs text-red-700">
                {acceptedCancelError}
              </p>
            )}
          </div>
        )}

        {passengerTrip.status === "Accepted" &&

          passengerTrip.driverArrivedAt &&

          !passengerTrip.otpVerified &&

          passengerTrip.startOtp && (

            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-center">

              <p className="text-xs font-bold text-amber-900">

                🔐 Give this pickup OTP to your DRIVER

              </p>

              <p className="text-2xl font-black tracking-widest text-slate-900">

                {passengerTrip.startOtp}

              </p>

            </div>

          )}

        {passengerTrip.status === "Started" && (
          <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <p className="text-xs font-semibold text-indigo-900">
              Reached your destination? Confirm only after actually arriving.
            </p>
            <button
              type="button"
              disabled={arrivalBusy}
              onClick={() => void confirmDestinationArrival()}
              className="w-full rounded-lg bg-emerald-700 px-3 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              {arrivalBusy ? "Confirming arrival..." : "🏁 Reached Destination"}
            </button>
          </div>
        )}
        {passengerTrip.status === "Completed" && (
          <div className="space-y-2 rounded-lg border border-emerald-300 bg-white p-3">
            <p role="status" className="text-sm font-bold text-emerald-800">
              ✅ Ride Completed — DRIVER has been notified.
            </p>
            <Link href="/my-ride-requests" className="block text-xs font-semibold text-indigo-700 underline">
              View completed ride and payment details
            </Link>
            <button
              type="button"
              onClick={dismissCompletedTrip}
              className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700"
            >
              Close completed ride card
            </button>
          </div>
        )}
        {arrivalError && (
          <p role="alert" className="text-xs font-semibold text-red-700">{arrivalError}</p>
        )}
        {passengerTrip.otpVerified && (

          <p className="text-xs font-semibold text-emerald-800">

            ✅ Pickup OTP verified

          </p>

        )}

      </div>

    )}

    {mode === "Now" && passengerTrip && passengerTrip.status !== "Completed" && !passengerTrip.driver?.driverLiveLocationUpdatedAt && (
      <p role="status" className="text-xs font-semibold text-amber-800">
        DRIVER live GPS temporarily unavailable. Booking and OTP remain accessible.
      </p>
    )}
    {mode === "Now" && passengerTripError && (

      <p role="alert" className="text-xs text-red-700">

        {passengerTripError}

      </p>

    )}



    <div className={
      showForm &&
      !(mode === "Now" && (
        (searchingTrip &&
          !!searchingTrip.rideNowExpiresAt &&
          new Date(searchingTrip.rideNowExpiresAt).getTime() > searchNowMs) ||
        passengerTrip?.status === "Accepted" ||
        passengerTrip?.status === "Started"
      ))
        ? "space-y-2"
        : "hidden"
    }>

    <p className="text-xs text-slate-600">{mode === "Now" ? "Request a ride from nearby drivers. Ride Now expires in 15 minutes." : "Request a ride for a chosen date. Journey time is optional."}</p>

    {mode === "Scheduled" && <div className="grid grid-cols-2 gap-2">

      <label className="text-xs font-semibold">Journey date<input id="homepage-request-date" type="date" value={date} disabled={blocked} onChange={event => setDate(event.target.value)} className="mt-1 block w-full rounded border border-slate-200 px-2 py-1 text-xs" /></label>

      <div><label htmlFor="homepage-request-hour" className="text-xs font-semibold">Time (optional)</label>

        <div className="mt-1 flex gap-1">

          <select id="homepage-request-hour" aria-label="Journey hour" value={hour} disabled={blocked} onChange={event => setHour(event.target.value)} className="min-w-0 flex-1 rounded border border-slate-200 py-1 text-xs"><option value="">Hour</option>{Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}</select>

          <select aria-label="Journey minute" value={minute} disabled={blocked} onChange={event => setMinute(event.target.value)} className="min-w-0 flex-1 rounded border border-slate-200 py-1 text-xs">{["00", "05", "10", "15", "20", "30", "45"].map(m => <option key={m} value={m}>{m}</option>)}</select>

          <select aria-label="AM or PM" value={period} disabled={blocked} onChange={event => setPeriod(event.target.value as "AM" | "PM")} className="min-w-0 flex-1 rounded border border-slate-200 py-1 text-xs"><option>AM</option><option>PM</option></select>

        </div>

      </div>

    </div>}

    {mode === "Now" && (
      <fieldset disabled={blocked} className="rounded-xl border border-slate-200 p-3">
        <legend className="px-1 text-xs font-bold">Booking Type</legend>
        <div className="flex gap-4 text-xs font-semibold">
          <label className="flex items-center gap-2">
            <input type="radio" name="ride-now-booking-type"
              checked={bookingType === "Seat"}
              onChange={() => setBookingType("Seat")} />
            🪑 Seat
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="ride-now-booking-type"
              checked={bookingType === "Parcel"}
              onChange={() => setBookingType("Parcel")} />
            📦 Parcel
          </label>
        </div>
        {bookingType === "Parcel" && (
          <div className="mt-3 space-y-2">
            <label className="block text-xs font-semibold">
              Parcel Details
              <textarea value={parcelDescription}
                maxLength={500}
                onChange={event => setParcelDescription(event.target.value)}
                placeholder="What is inside the parcel?"
                className="mt-1 w-full rounded border border-slate-200 p-2 text-xs" />
            </label>
            <label className="block text-xs font-semibold">
              Approximate Weight (kg)
              <input type="number" min="0.1" max="100" step="0.1"
                value={parcelWeightKg}
                onChange={event => setParcelWeightKg(event.target.value)}
                className="mt-1 w-full rounded border border-slate-200 p-2 text-xs" />
            </label>
            <label className="block text-xs font-semibold">
              📞 Receiver Mobile Number
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
                value={receiverPhone}
                onChange={event =>
                  setReceiverPhone(event.target.value.replace(/\\D/g, "").slice(0, 10))
                }
                placeholder="10-digit receiver mobile number"
                className="mt-1 w-full rounded border border-slate-200 p-2 text-xs"
              />
            </label>
            <label className="block text-xs font-semibold">
              📸 Parcel Photo
              <input type="file" accept="image/jpeg,image/png,image/webp"
                onChange={event => {
                  const file = event.target.files?.[0] || null;
                  const compressionId = ++parcelCompressionId.current;
                  setParcelCompressing(false);
                  if (parcelPreview) URL.revokeObjectURL(parcelPreview);
                  setParcelPhoto(null);
                  setParcelPreview(null);

                  if (!file) return;

                  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)
                      || file.size <= 0 || file.size > 5 * 1024 * 1024) {
                    setError("Choose a JPG, PNG or WebP photo up to 5 MB.");
                    event.target.value = "";
                    return;
                  }

                  setError("");
                  setParcelCompressing(true);
                  void compressDriverDocument(file)
                    .then(compressed => {
                      if (compressionId !== parcelCompressionId.current) return;
                      if (compressed.size > 100 * 1024) {
                        throw new Error("Parcel photo must be within 100 KB.");
                      }
                      setParcelPhoto(compressed);
                      setParcelPreview(URL.createObjectURL(compressed));
                    })
                    .catch(cause => {
                      if (compressionId !== parcelCompressionId.current) return;
                      setError(cause instanceof Error
                        ? cause.message : "Parcel photo compression failed.");
                    })
                    .finally(() => {
                      if (compressionId === parcelCompressionId.current) {
                        setParcelCompressing(false);
                      }
                    });
                }}
                className="mt-1 block w-full text-xs" />
            </label>
            {parcelCompressing && (
              <p role="status" className="text-xs text-indigo-700">
                Compressing parcel photo to 100 KB...
              </p>
            )}
            {parcelPreview && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={parcelPreview} alt="Selected parcel photo"
                className="h-32 w-32 rounded-lg border object-cover" />
            )}
          </div>
        )}
      </fieldset>
    )}

    <div className="grid grid-cols-3 gap-2">

      {(mode !== "Now" || bookingType === "Seat") && (
        <label className="text-xs font-semibold">
          Seats
          <input id="homepage-request-seats" type="number" min="1" max="20"
            value={seats} disabled={blocked}
            onChange={event => setSeats(event.target.value)}
            className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs" />
        </label>
      )}

      <label className="text-xs font-semibold">Vehicle<select id="homepage-request-vehicle" value={vehicle} disabled={blocked} onChange={event => setVehicle(event.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs"><option value="Any">Any</option><option>Car</option><option>Bike</option><option>SUV</option></select></label>

      <label className="text-xs font-semibold">Contribution (₹)<input id="homepage-request-contribution" type="number" min="0" step="any" placeholder="Optional" value={contribution} disabled={blocked} onChange={event => setContribution(event.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs" /></label>

    </div>

    {!exactTrip && <p className="text-xs text-slate-500">Select pickup and destination using search or the map.</p>}

    {exactTrip && (!currentRoute || currentRoute.loading) && <p role="status" className="text-xs text-slate-500">Calculating road route...</p>}

    {exactTrip && currentRoute?.error && <p role="alert" className="text-xs text-red-700">{currentRoute.error} <button type="button" onClick={() => setRefreshRoute(n => n + 1)} className="font-semibold underline">Retry route</button></p>}

    {estimate && <p className="text-xs text-indigo-700">Road route: {estimate.distanceKm} km · {estimate.durationMinutes} min</p>}

    <p className="text-[11px] text-emerald-700">No advance payment required.</p>

    {!loggedIn || needsLogin ? <Link href="/login" className="block text-xs font-semibold text-indigo-700 underline">Log in to send a request</Link> : null}

    {!review && error && <p role="alert" className="text-xs text-red-700">{error}</p>}

    {status ? <div><p role="status" className="text-xs text-emerald-800">{status}</p><Link href="/my-ride-requests" className="text-xs font-semibold text-indigo-700 underline">View My Requests</Link></div> : <button type="button" onClick={openReview} disabled={!loggedIn || needsLogin || !estimate || blocked ||
  (mode === "Now" && bookingType === "Parcel" &&
    (parcelCompressing || !parcelPhoto))} className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">Review {totalLabel}</button>}

    {review && <dialog ref={dialogRef} aria-labelledby="ride-request-review-title" onCancel={event => { event.preventDefault(); closeReview(); }} className="fixed inset-0 m-auto max-h-[75dvh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-2xl bg-white p-4 text-slate-900 shadow-2xl backdrop:bg-slate-900/40">

      <div className="flex justify-between gap-3"><h3 id="ride-request-review-title" className="text-sm font-bold">{posted ? "Request posted" : `Confirm ${review.mode === "Now" ? "Ride Now" : "Scheduled Request"}`}</h3><button type="button" disabled={pending} onClick={closeReview} className="text-xs font-semibold text-indigo-700 disabled:opacity-50">Close</button></div>

      <p className="mt-3 break-words text-xs">{review.pickup?.label} → {review.destination?.label}</p>

      <p className="mt-2 text-xs">
        {review.bookingType === "Parcel"
          ? `📦 Parcel · ${review.parcelWeightKg || "Weight missing"} kg · ${review.parcelDescription || "Details missing"} · Receiver: ${review.receiverPhone || "Number missing"}`
          : `🪑 ${review.seats} seat(s)`}
        {" · "}{review.vehicle} vehicle
        {" · "}{review.contribution.trim() === "" ? "Contribution open" : `₹${review.contribution} contribution`}
      </p>

      <p className="mt-2 text-xs">{review.mode === "Now" ? "Ride Now · 15-minute request" : `${review.date} · ${review.hour ? `${review.hour}:${review.minute} ${review.period}` : "Time flexible"}`}</p>

      {error && <p role="alert" className="mt-3 text-xs text-red-700">{error}</p>}

      {status && <p role="status" className="mt-3 text-xs text-emerald-800">{status}</p>}

      {(posted || uncertain) ? <Link href="/my-ride-requests" className="mt-3 block rounded-lg bg-indigo-600 p-2 text-center text-xs font-semibold text-white">View My Requests</Link> : <button type="button" onClick={() => void confirmRequest()} disabled={pending || needsLogin} className="mt-3 w-full rounded-lg bg-indigo-600 p-2 text-xs font-semibold text-white disabled:opacity-50">{pending ? "Sending request..." : review.mode === "Now" ? "Confirm & Broadcast Ride Now" : "Confirm & Post Scheduled Request"}</button>}

      {needsLogin && <Link href="/login" className="mt-2 block text-xs font-semibold text-indigo-700">Log in to continue</Link>}

    </dialog>}

    </div>

  </div>;

}
